import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { restoreCsrTheme } from '../../csr/csr-theme';
import {
  SupervisorService, AgentStatus, EscalationItem
} from '../../../core/services/supervisor.service';
import { CsrCallService } from '../../../core/services/csr-call.service';

interface SuperAgent {
  name: string; initials: string; ext: string;
  status: 'On Call' | 'Available' | 'Away' | 'Offline';
  role: string;
  callTime: string; handled: number | string; avg: string;
  gradFrom: string; gradTo: string;
}

interface Escalation {
  id: string; numId: number; issue: string; dept: string;
  citizen: string; time: string; critical: boolean;
}

interface ModalLiveCall {
  id: string; citizen: string; dept: string; agent: string; agentExt: string; dur: string; pri: string;
}

interface ModalQueueItem {
  pos: number; citizen: string; dept: string; wait: string; ch: string; pri: string;
}

interface StatCard { label: string; val: string; col: string; icon: string; }
interface DeptData  { dept: string; pct: number; col: string; count?: number; }

@Component({
  selector: 'app-supervisor-control-room',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './supervisor-control-room.component.html',
  styleUrls: ['./supervisor-control-room.component.scss']
})
export class SupervisorControlRoomComponent implements OnInit, OnDestroy {
  clock = '';
  showBroadcast = false;
  broadcastMsg = '';
  handledEscIds: string[] = [];
  activeStatModal: string | null = null;
  toastMsg: string | null = null;

  private clockInterval?: ReturnType<typeof setInterval>;
  private pollInterval?: ReturnType<typeof setInterval>;
  private toastTimeout?: ReturnType<typeof setTimeout>;
  private subs = new Subscription();

  stats: StatCard[] = [
    { label: 'Active Agents',     val: '—', col: 'blue',   icon: 'headset_mic'   },
    { label: 'Live Calls',        val: '—', col: 'green',  icon: 'phone_in_talk' },
    { label: 'In Queue',          val: '—', col: 'amber',  icon: 'layers'        },
    { label: 'Avg Handle',        val: '—', col: 'cyan',   icon: 'timer'         },
    { label: 'Timeline Breaches', val: '—', col: 'red',    icon: 'warning_amber' },
    { label: 'Resolved Today',    val: '—', col: 'purple', icon: 'check_circle'  },
  ];

  agents: SuperAgent[] = [];
  escalations: Escalation[] = [];
  hourData: number[] = [];
  deptData: DeptData[] = [];
  liveCalls: ModalLiveCall[] = [];
  queueItems: ModalQueueItem[] = [];
  coachingExt = '';
  coachingPending = false;
  monitoringExt = '';
  monitoringPending = false;
  private assistConnectTimeout: ReturnType<typeof setTimeout> | null = null;
  resolvedByDept: DeptData[] = [];

  readonly breaches = [
    { id: '—', title: 'No breach data loaded', dept: '—', sla: '—', elapsed: '—', agent: '—', breachBy: '—' },
  ];

  get activeAgentsCount(): string { return this.stats[0].val; }
  get escalationsCount(): number  { return this.escalations.length; }

  constructor(private supervisor: SupervisorService, private readonly csrCall: CsrCallService) {}

  ngOnInit(): void {
    restoreCsrTheme();
    this.clock = new Date().toLocaleTimeString();
    this.clockInterval = setInterval(() => { this.clock = new Date().toLocaleTimeString(); }, 1000);

    this.supervisor.fetchStats();
    this.supervisor.fetchLiveCalls();
    this.supervisor.fetchEscalations();
    this.supervisor.fetchQueue();
    this.supervisor.fetchHourly();
    this.supervisor.fetchCategory();
    this.supervisor.fetchAgents();

    this.pollInterval = setInterval(() => {
      this.supervisor.fetchStats();
      this.supervisor.fetchLiveCalls();
      this.supervisor.fetchQueue();
    }, 15000);

    this.subs.add(this.supervisor.stats$.subscribe(s => {
      if (!s) return;
      this.stats[0].val = String(s.activeAgents);
      this.stats[1].val = String(s.liveCalls);
      this.stats[2].val = String(s.inQueue);
      this.stats[3].val = this.fmtSecs(s.avgHandleSeconds);
      this.stats[4].val = String(s.timelineBreaches);
      this.stats[5].val = String(s.resolvedToday);
    }));

    this.subs.add(this.supervisor.agents$.subscribe(list => {
      this.agents = list.filter(a => this.isControlRoomAgent(a)).map(a => this.mapAgent(a));
    }));

    this.subs.add(this.supervisor.escalations$.subscribe(list => {
      this.escalations = list.map(e => this.mapEscalation(e));
    }));

    this.subs.add(this.supervisor.hourly$.subscribe(data => {
      const max = Math.max(...data.map(d => d.count), 1);
      this.hourData = data.map(d => Math.round((d.count / max) * 100));
    }));

    this.subs.add(this.supervisor.category$.subscribe(data => {
      const colors = ['blue', 'amber', 'green', 'purple', 'cyan', 'red'];
      this.deptData = data.map((d, i) => ({
        dept: d.name, pct: d.pct, col: colors[i % colors.length], count: d.count,
      }));
      this.resolvedByDept = this.deptData.map(d => ({ ...d }));
    }));

    this.subs.add(this.supervisor.liveCalls$.subscribe(list => {
      this.liveCalls = list.map(c => ({
        id: c.callerNumber || c.channelId,
        citizen: c.callerNumber,
        dept: c.callStatus,
        agent: c.agentName || c.agentExtension,
        agentExt: c.agentExtension,
        dur: this.fmtSecs(c.durationSeconds),
        pri: c.durationSeconds > 300 ? 'critical' : c.durationSeconds > 120 ? 'high' : 'medium',
      }));
    }));

    this.subs.add(this.supervisor.queue$.subscribe(list => {
      this.queueItems = list.map((q, i) => ({
        pos: i + 1,
        citizen: q.callerNumber,
        dept: q.topic || q.channelType,
        wait: this.fmtSecs(q.waitSeconds),
        ch: q.channelType,
        pri: q.priority.toLowerCase(),
      }));
    }));

    this.subs.add(this.csrCall.isCoaching$.subscribe(active => {
      if (active) { this.coachingPending = false; this.clearAssistTimeout(); }
    }));

    this.subs.add(this.csrCall.isMonitoring$.subscribe(active => {
      if (active) { this.monitoringPending = false; this.clearAssistTimeout(); }
    }));

    // Backend reported the session failed (call ended, bridge error, …) — reset the UI.
    this.subs.add(this.csrCall.assistFailed$.subscribe(reason => {
      if (this.coachingPending || this.monitoringPending) {
        this.coachingExt = ''; this.coachingPending = false;
        this.monitoringExt = ''; this.monitoringPending = false;
        this.clearAssistTimeout();
        this.showToast(reason || 'Session failed — please retry');
      }
    }));
  }

  ngOnDestroy(): void {
    if (this.clockInterval) clearInterval(this.clockInterval);
    if (this.pollInterval)  clearInterval(this.pollInterval);
    if (this.toastTimeout)  clearTimeout(this.toastTimeout);
    this.clearAssistTimeout();
    this.subs.unsubscribe();
  }

  /**
   * Sequential pre-flight shared by coach and monitor. Each gate must pass before
   * the next runs; returns the supervisor ext on success, null (with a toast) on failure.
   */
  private async assistPreflight(action: string): Promise<string | null> {
    // Gate 1: extension reserved
    const supervisorExt = this.csrCall.myExt$.value;
    if (!supervisorExt) { this.showToast(`Reserve a SIP extension before ${action}`); return null; }
    // Gate 2: SIP registered with Asterisk
    if (this.csrCall.sipStatus$.value !== 'registered') {
      this.showToast('SIP not registered — open the CSR console first to activate your SIP extension');
      return null;
    }
    // Gate 3: another session already in progress
    if (this.coachingExt || this.monitoringExt) {
      this.showToast('Stop the current coach/monitor session first');
      return null;
    }
    // Gate 4: microphone permission — request it now, inside the button click,
    // so the browser shows the prompt and the later auto-answer cannot fail on it.
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(t => t.stop());
    } catch {
      this.showToast('Microphone access is required — allow mic permission in your browser and try again');
      return null;
    }
    return supervisorExt;
  }

  private armAssistTimeout(kind: 'coach' | 'monitor'): void {
    this.clearAssistTimeout();
    this.assistConnectTimeout = setTimeout(() => {
      const stillPending = kind === 'coach' ? this.coachingPending : this.monitoringPending;
      if (!stillPending) return;
      this.showToast('Could not connect within 15s — session cancelled, please try again');
      if (kind === 'coach') this.stopCoach(); else this.stopMonitor();
    }, 15000);
  }

  private clearAssistTimeout(): void {
    if (this.assistConnectTimeout) { clearTimeout(this.assistConnectTimeout); this.assistConnectTimeout = null; }
  }

  async startCoach(agentExt: string): Promise<void> {
    const supervisorExt = await this.assistPreflight('coaching');
    if (!supervisorExt) return;
    this.coachingExt = agentExt;
    this.coachingPending = true;
    this.showToast(`Requesting coach session for Ext ${agentExt}...`);
    this.armAssistTimeout('coach');
    this.supervisor.startWhisperCoach(agentExt, supervisorExt).subscribe(res => {
      if (res?.error) {
        this.coachingExt = '';
        this.coachingPending = false;
        this.clearAssistTimeout();
        this.showToast(res.message || 'Coach session could not be started');
        return;
      }
      this.showToast(`Coaching Ext ${agentExt} — connecting...`);
    });
  }

  stopCoach(): void {
    const ext = this.coachingExt;
    this.coachingExt = '';
    this.coachingPending = false;
    this.clearAssistTimeout();
    this.csrCall.terminateAssistSession();
    if (ext) { this.supervisor.stopWhisperCoach(ext); this.showToast('Coaching session ended'); }
  }

  async startMonitor(agentExt: string): Promise<void> {
    const supervisorExt = await this.assistPreflight('monitoring');
    if (!supervisorExt) return;
    this.monitoringExt = agentExt;
    this.monitoringPending = true;
    this.showToast(`Requesting monitor session for Ext ${agentExt}...`);
    this.armAssistTimeout('monitor');
    this.supervisor.startMonitor(agentExt, supervisorExt).subscribe(res => {
      if (res?.error) {
        this.monitoringExt = '';
        this.monitoringPending = false;
        this.clearAssistTimeout();
        this.showToast(res.message || 'Monitor session could not be started');
        return;
      }
      this.showToast(`Monitoring Ext ${agentExt} — connecting...`);
    });
  }

  stopMonitor(): void {
    const ext = this.monitoringExt;
    this.monitoringExt = '';
    this.monitoringPending = false;
    this.clearAssistTimeout();
    this.csrCall.terminateAssistSession();
    if (ext) { this.supervisor.stopMonitor(ext); this.showToast('Monitoring session ended'); }
  }

  sendBroadcast(): void {
    if (!this.broadcastMsg.trim()) return;
    const msg = this.broadcastMsg.trim();
    this.showBroadcast = false;
    this.broadcastMsg = '';
    this.supervisor.sendBroadcast(msg);
    this.showToast('Broadcast sent to all agents');
  }

  handleEscalation(id: string): void {
    if (this.handledEscIds.includes(id)) return;
    const esc = this.escalations.find(e => e.id === id);
    if (!esc) return;
    this.handledEscIds = [...this.handledEscIds, id];
    this.supervisor.handleEscalation(esc.numId);
    this.showToast(`Escalation ${id} — assigned for handling`);
  }

  openStatModal(label: string): void { this.activeStatModal = label; }
  closeStatModal(): void             { this.activeStatModal = null;  }

  showToast(msg: string): void {
    this.toastMsg = msg;
    if (this.toastTimeout) clearTimeout(this.toastTimeout);
    this.toastTimeout = setTimeout(() => { this.toastMsg = null; }, 3000);
  }

  agentStripe(status: string): string {
    return status === 'On Call' ? 'stripe-blue' : status === 'Available' ? 'stripe-green'
         : status === 'Away'    ? 'stripe-amber' : 'stripe-gray';
  }

  agentBadge(status: string): string {
    return status === 'On Call' ? 'sb-blue' : status === 'Available' ? 'sb-green'
         : status === 'Away'    ? 'sb-amber' : 'sb-gray';
  }

  barHeight(v: number): string { return `${v}px`; }

  progColor(col: string): string { return `pf-${col}`; }

  resolvedColor(col: string): string {
    const m: Record<string, string> = {
      blue: 'var(--csr-accent)', amber: '#ffa726', green: '#66bb6a',
      purple: '#ab47bc', cyan: '#26c6da', red: '#ef5350',
    };
    return m[col] ?? 'var(--csr-accent)';
  }

  private readonly gradients = [
    ['#4f8ef7', '#8b5cf6'], ['#00c48c', '#4f8ef7'], ['#ffb020', '#ff5b5b'],
    ['#8b5cf6', '#ec4899'], ['#00d4ff', '#00c48c'], ['#ff5b5b', '#ffb020'],
    ['#4f8ef7', '#00d4ff'], ['#7b8fa6', '#4a5568'],
  ] as const;

  get agentOnCallCount():   number { return this.agents.filter(a => a.status === 'On Call').length; }
  get agentAvailableCount(): number { return this.agents.filter(a => a.status === 'Available').length; }
  get agentOfflineCount():  number { return this.agents.filter(a => a.status === 'Offline').length; }

  get agentRoleGroups(): Array<{ role: string; count: number; onCall: number; statusLabel: string }> {
    const groups = new Map<string, { count: number; onCall: number }>();
    for (const a of this.agents) {
      const role = a.role || 'CSR';
      const g = groups.get(role) ?? { count: 0, onCall: 0 };
      g.count++;
      if (a.status === 'On Call') g.onCall++;
      groups.set(role, g);
    }
    return Array.from(groups.entries()).map(([role, g]) => ({
      role: role.charAt(0) + role.slice(1).toLowerCase().replace('_', ' '),
      count: g.count,
      onCall: g.onCall,
      statusLabel: g.onCall === g.count ? 'On Call' : g.onCall > 0 ? 'Mixed' : 'Available',
    }));
  }

  private mapAgent(a: AgentStatus): SuperAgent {
    const parts    = a.fullName.trim().split(/\s+/);
    const initials = parts.slice(0, 2).map(w => w[0] ?? '').join('').toUpperCase();
    const gradIdx  = parseInt(a.sipExtension.replace(/\D/g, '') || '0') % this.gradients.length;
    const [gradFrom, gradTo] = this.gradients[gradIdx];
    const status = a.status === 'ON_CALL' ? 'On Call' : a.status === 'OFFLINE' ? 'Offline' : 'Available';
    return {
      name: a.fullName, initials, ext: a.sipExtension,
      status, role: a.role ?? 'CSR',
      callTime: '—', handled: '—', avg: '—',
      gradFrom, gradTo,
    };
  }

  private isControlRoomAgent(agent: AgentStatus): boolean {
    if (agent.role === 'PUBLIC_USER') return false;
    return !/^3\d{3}$/.test(agent.sipExtension || '');
  }

  private mapEscalation(e: EscalationItem): Escalation {
    const time = e.overdueDays > 0 ? `${e.overdueDays}d overdue` : 'Pending';
    return {
      id: e.complaintNo, numId: e.complaintId,
      issue: e.issue, dept: 'Complaint',
      citizen: e.citizenName, time,
      critical: e.overdueDays > 1 || e.priority === 'HIGH' || e.priority === 'CRITICAL',
    };
  }

  private fmtSecs(seconds: number): string {
    if (!seconds || seconds < 0) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }
}
